import {HeroBrandsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {TrackComponentParamsType} from '@aboutyou/tracking';
import {FadeTransition} from '@aboutyou/transitions';
import {
  RowSection,
  desktopAndUpLinaria,
  extraLargeScreensLinaria,
  higherDesktopAndUpLinaria,
  largeScreensLinaria,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {TextBanner} from '../../../Recommendations/shared';
import {HeroBrandsList} from './HeroBrandsList';
import {HeroContent} from './HeroContent/HeroContent';
import {useHeroBrandsSlider} from './utils/useHeroBrandsSlider';

const ANIMATION_DURATION_MS = 800;

export const HeroBrandsSlider: React.FC<
  HeroBrandsSection & {
    streamPosition: number;
    onAddToBasketCtaClick?: ({
      productId,
      locationId,
    }: {
      productId: number;
      locationId?: string;
      couponPromotionId?: string;
    }) => void;
    allBrandsUrl?: string;
    trackProductListSwipeEvent?: boolean;
    className?: string;
    trackComponentConfig: TrackComponentParamsType;
  }
> = ({
  topline,
  headline,
  elements,
  streamPosition,
  onAddToBasketCtaClick,
  allBrandsUrl,
  trackProductListSwipeEvent,
  className,
  trackComponentConfig,
}) => {
  const {
    activeBrand,
    activeBrandIndex,
    changeSlide,
    paused,
    pauseTimer,
    preloadBrandContent,
    ref,
    uniqueKey,
  } = useHeroBrandsSlider(elements);

  return (
    <Wrapper
      className={className}
      pullEnd
      ref={ref}
      data-testid="heroBrandsSlider"
    >
      <StyledTextBanner headline={headline} subline={topline} />
      <HeroBrandsList
        onClick={pauseTimer}
        onBrandClick={changeSlide}
        onBrandMouseDown={preloadBrandContent}
        activeBrandIndex={activeBrandIndex}
        brands={elements.map(element => ({
          ...element.brand,
          ...element.header,
          image: element.coverLandscapeImage.url
            ? element.coverLandscapeImage
            : element.coverPortraitImage,
        }))}
        isPaused={paused}
        allBrandsUrl={allBrandsUrl}
      />

      <HeroContentWrapper>
        <StyledFadeTransition
          duration={ANIMATION_DURATION_MS}
          uniqueKey={`hero-brands-${streamPosition}-${activeBrandIndex}`}
        >
          <StyledHeroContent
            streamPosition={streamPosition}
            onClick={pauseTimer}
            onAddToBasketCtaClick={onAddToBasketCtaClick}
            activeBrandIndex={activeBrandIndex}
            trackProductListSwipeEvent={trackProductListSwipeEvent}
            uniqueKey={uniqueKey}
            trackComponentConfig={trackComponentConfig}
            sponsored={activeBrand.sponsored}
            {...activeBrand}
          />
        </StyledFadeTransition>
      </HeroContentWrapper>
    </Wrapper>
  );
};

const HeroContentWrapper = styled.div`
  --cover-image-height: 180px;

  position: relative;
  height: clamp(540px, 65vh, 580px);

  ${tabletAndUpLinaria(`height: clamp(380px, 50vh, 400px);`)};
  ${desktopAndUpLinaria(`height: clamp(450px, 50vh, 500px);`)};
  ${higherDesktopAndUpLinaria(`height: clamp(520px, 50vh, 580px);`)};
  ${largeScreensLinaria(`height: clamp(550px, 60vh, 580px);`)};
  ${extraLargeScreensLinaria(`height: clamp(600px, 70vh, 650px);`)};
`;

const StyledHeroContent = styled(HeroContent)`
  height: 100%;
`;

const Wrapper = styled(RowSection)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mobileOnlyLinaria(`
    gap: 16px;
    padding-inline: 16px;
  `)}
`;

const StyledFadeTransition = styled(FadeTransition)`
  height: 100%;
`;

const StyledTextBanner = styled(TextBanner)`
  ${mobileOnlyLinaria(`
    margin-bottom: 0;
  `)}
`;
